/**
 * dcent-x-sign-screen.js — 송금 확인(Send) 화면의 타이핑 시퀀스. (2026-09-07 Message Sign → Send 교체 — 스니펫 주석 참조)
 *
 * 마크업은 `snippets/dcent-x-sign-screen.liquid`, 외형은 `dcent-x-sign-screen.css`.
 * 원본: `20260827 homepage_전달용/src/components/SignScreenDevice.tsx` 의 useEffect 2개.
 *
 * 시퀀스 (원본이 사용자 조율로 확정한 타이밍 그대로)
 *   0            시작 대기 500ms
 *   1..TOTAL     유닛을 하나씩 켠다 — Type 70ms · Address 110ms · Amount/Network 80ms
 *   TOTAL        완성 홀드 800ms
 *   PRESSED      confirm 탭 420ms (축소 + 흰 링 — CSS 가 그린다)
 *   FP           지문 화면 4400ms (FingerprintScan 자체 주기와 맞춘 값)
 *   → 처음으로
 *
 * ⚠️ **reduced-motion 이면 타이핑을 건너뛰고 완성 화면을 보여준다.** 그냥 멈추면 값이 전부
 * 숨겨진(opacity 0) 빈 화면이 남아 "기기가 거래 내용을 직접 그려준다"는 요지가 사라진다.
 * 모션만 빼고 정보는 남기는 것이 이 페이지 전반의 규약이다.
 *
 * 유닛 개수·구간 경계는 **마크업이 data-* 로 알려준다**(data-total·data-sign-n·data-addr-n).
 * 문자열을 여기서도 세면 스니펫과 어긋날 때 조용히 타이밍만 틀어진다.
 *
 * 안전 규약: 대상 없으면 조용히 종료 · 멱등 설치 · 보일 때만 재생 · 이탈 시 리셋.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */

if (!window.__dcentXSignScreen) {
  window.__dcentXSignScreen = true;

  const HOLD_START = 500;
  const HOLD_DONE = 800;
  const HOLD_PRESS = 420;
  const HOLD_FP = 4400;
  const T_SIGN = 70;
  const T_ADDR = 110;
  const T_MSG = 80;

  const setup = (root) => {
    const total = Number(root.dataset.total) || 0;
    const signN = Number(root.dataset.signN) || 0;
    const addrN = Number(root.dataset.addrN) || 0;
    const units = Array.from(root.querySelectorAll("[data-i]"));
    if (!total || !units.length) return;

    const PRESSED = total + 1;
    const FP = total + 2;

    /** p 단계를 화면에 반영한다. CSS 가 못 하는 일(유닛별 on·커서 위치)만 여기서 한다. */
    const paint = (p, still) => {
      const cursorAt = still || p === 0 || p >= total ? -1 : p - 1;
      units.forEach((u) => {
        const i = Number(u.dataset.i);
        u.dataset.on = i < p ? "1" : "0";
        if (i === cursorAt) u.dataset.cursor = "1";
        else u.removeAttribute("data-cursor");
      });
      root.dataset.phase = p === PRESSED ? "pressed" : p === FP ? "fp" : "type";
    };

    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      paint(total, true); // 완성 화면으로 정지 — 빈 화면을 남기지 않는다
      return;
    }

    let p = 0;
    let timer = 0;
    const delayFor = (v) => {
      if (v === 0) return HOLD_START;
      if (v === total) return HOLD_DONE;
      if (v === PRESSED) return HOLD_PRESS;
      if (v === FP) return HOLD_FP;
      if (v < signN) return T_SIGN;
      if (v < signN + addrN) return T_ADDR;
      return T_MSG;
    };
    const tick = () => {
      p = p >= FP ? 0 : p + 1;
      paint(p, false);
      timer = window.setTimeout(tick, delayFor(p));
    };

    paint(0, false);

    if (!("IntersectionObserver" in window)) {
      timer = window.setTimeout(tick, delayFor(0));
      return;
    }
    // threshold 0.15 — 크롭 배치라 0.4 는 넘지 못하는 경우가 있다(원본과 같은 값)
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (!timer) timer = window.setTimeout(tick, delayFor(p));
        } else {
          window.clearTimeout(timer);
          timer = 0;
          p = 0;
          paint(0, false); // 이탈 시 리셋 — 재진입마다 처음부터
        }
      },
      { threshold: 0.15 },
    );
    io.observe(root);
  };

  const init = () => {
    document.querySelectorAll("[data-x-sign]").forEach((root) => {
      if (root.dataset.xSignReady === "1") return; // 멱등
      root.dataset.xSignReady = "1";
      setup(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
